/**
 * API Service (MIRA 2.0)
 *
 * Native fetch with MIRA 2.0 conventions:
 *   - Auth: the session token MIRA returns when the portal exchanges its
 *     `?token=` (see portalService.exchangePortalToken and portalSession.ts),
 *     sent in the X-Portal-Session header. No cookies (`credentials: 'omit'`),
 *     no API key, no X-Employee-Id.
 *   - Response envelope: {success, data, error?: {message, code}, errors?, meta?}
 */

import { clearPortalSession, getPortalSession } from './portalSession';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api-staging.mira.parkstyling.dk';

/** Stable error codes returned by MIRA 2.0 (subset we care about in the UI). */
export type ApiErrorCode =
  | 'PORTAL_TOKEN_INVALID'
  | 'PORTAL_SESSION_INVALID'
  | 'PORTAL_AUTH_REQUIRED'
  | 'PORTAL_ORIGIN_FORBIDDEN'
  | 'API_KEY_REQUIRED'
  | 'SCOPE_DENIED'
  | 'EMPLOYEE_NOT_FOUND'
  | 'ADMIN_MODE_REQUIRED'
  | 'VALIDATION_ERROR'
  | 'FORBIDDEN'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  | 'COURSE_NOT_FOUND'
  | 'COURSE_INACTIVE'
  | 'COURSE_PAST'
  | 'ALREADY_ENROLLED'
  | 'CAPACITY_FULL'
  | 'INVALID_PORTAL_STATE'
  | 'ENROLLMENT_NOT_FOUND'
  | 'OWNER_OR_SELF_REQUIRED'
  | 'OWNER_REQUIRED'
  | 'CROSS_TENANT'
  | 'INVALID_STATUS_TRANSITION'
  | 'DEADLINE_PASSED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export class ApiError extends Error {
  code: ApiErrorCode | string;
  statusCode: number;
  /** Validation errors (HTTP 422) keyed by field path. */
  fieldErrors?: Record<string, string[]>;
  /** Seconds to wait, present on 429 responses. */
  retryAfter?: number;

  constructor(
    message: string,
    code: ApiErrorCode | string,
    statusCode: number,
    fieldErrors?: Record<string, string[]>,
    retryAfter?: number
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
    this.retryAfter = retryAfter;
  }
}

interface RequestConfig {
  /** Optional query params. */
  query?: Record<string, string | number | boolean | undefined>;
  /** Optional JSON body (POST/DELETE/PUT). */
  body?: Record<string, unknown>;
  /** Optional multipart body. Mutually exclusive with `body`. Content-Type is NOT set (browser adds boundary). */
  formData?: FormData;
  /** Optional Idempotency-Key header (POST). */
  idempotencyKey?: string;
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: { message: string; code: string };
  errors?: Record<string, string[]>;
  meta?: { current_page: number; per_page: number; total: number; last_page: number };
}

function buildUrl(path: string, query?: RequestConfig['query']): string {
  const url = new URL(`${API_BASE_URL}/api/v1${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  config: RequestConfig
): Promise<T> {
  const url = buildUrl(path, config.query);

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (config.body !== undefined) headers['Content-Type'] = 'application/json';
  if (config.idempotencyKey) headers['Idempotency-Key'] = config.idempotencyKey;
  const sessionToken = getPortalSession();
  if (sessionToken) headers['X-Portal-Session'] = sessionToken;

  const requestBody: BodyInit | undefined =
    config.formData !== undefined
      ? config.formData
      : config.body !== undefined
        ? JSON.stringify(config.body)
        : undefined;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: requestBody,
      credentials: 'omit',
    });
  } catch {
    throw new ApiError(
      'Kunne ikke oprette forbindelse til serveren',
      'NETWORK_ERROR',
      0
    );
  }

  let envelope: Envelope<T> | null = null;
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    // Non-JSON response (e.g., 502 from a proxy). Fall through to error path.
  }

  if (!response.ok || !envelope?.success) {
    const rawMessage = envelope?.error?.message ?? 'Der opstod en fejl';
    const code = envelope?.error?.code ?? 'UNKNOWN';
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : undefined;

    // Mask server-internal errors (5xx, SERVER_ERROR, UNKNOWN) behind a friendly
    // message so users don't see raw stack traces or framework messages.
    // Domain-level 4xx codes (VALIDATION_ERROR, ENROLLMENT_NOT_FOUND, etc.)
    // pass through unchanged because their messages are actionable.
    const isServerInternal =
      response.status >= 500 || code === 'SERVER_ERROR' || code === 'UNKNOWN';
    const message = isServerInternal
      ? 'Der opstod en fejl. Prøv igen om et øjeblik. Hvis problemet fortsætter, kontakt os på info@parkstyling.com.'
      : rawMessage;

    if (isServerInternal) {
      // eslint-disable-next-line no-console
      console.error('[api] server error', { status: response.status, code, rawMessage });
    }

    const error = new ApiError(
      message,
      code,
      response.status,
      envelope?.errors,
      Number.isFinite(retryAfter) ? retryAfter : undefined
    );
    if (isAuthError(error)) {
      // The stored session is dead; a reload must not keep sending it.
      clearPortalSession();
      authErrorHandler?.(error);
    }
    throw error;
  }

  return envelope.data as T;
}

export function apiGet<T>(path: string, config: RequestConfig = {}): Promise<T> {
  return request<T>('GET', path, config);
}

export function apiPost<T>(path: string, config: RequestConfig = {}): Promise<T> {
  return request<T>('POST', path, config);
}

export function apiDelete<T>(path: string, config: RequestConfig = {}): Promise<T> {
  return request<T>('DELETE', path, config);
}

/** POST med multipart/form-data (billed-upload). Browseren sætter Content-Type/boundary. */
export function apiPostForm<T>(path: string, config: RequestConfig = {}): Promise<T> {
  return request<T>('POST', path, config);
}

const AUTH_ERROR_CODES: readonly string[] = [
  'PORTAL_TOKEN_INVALID',
  'PORTAL_SESSION_INVALID',
  'PORTAL_AUTH_REQUIRED',
  // MIRA's svar fra legacy-stien, når et kald mangler X-Portal-Session, indtil
  // nøglestien slås fra ved omstillingen. Derefter svarer MIRA PORTAL_AUTH_REQUIRED.
  'API_KEY_REQUIRED',
  'EMPLOYEE_NOT_FOUND',
];

/** Convenience: returns true if the error is one we should react to with re-auth UX. */
export function isAuthError(err: unknown): err is ApiError {
  return err instanceof ApiError && AUTH_ERROR_CODES.includes(err.code);
}

let authErrorHandler: ((err: ApiError) => void) | null = null;

/**
 * Registers the one place that reacts when the session is gone (expired,
 * revoked or never established). `request()` calls it once, right before it
 * throws an auth error. Pass `null` to unregister.
 */
export function setAuthErrorHandler(handler: ((err: ApiError) => void) | null): void {
  authErrorHandler = handler;
}
